// Sections of the candidate review page (docs/08-review-console.md). Render only; every number
// and flag is computed by the backend.
import type {
  AgentFluency,
  AnswerCard,
  CandidateReview,
  Flag,
  QuestionMetrics,
} from "../api/review";

const seconds = (ms: number | null | undefined) =>
  ms == null ? "—" : `${(ms / 1000).toFixed(1)} s`;

export function Header({ review }: { review: CandidateReview }) {
  const h = review.header;
  const employment = h.current_employment as { org?: string; role?: string } | null;
  const links = Object.entries(h.links).filter(([, v]) => typeof v === "string" && v);
  return (
    <section id="header">
      <h1>
        {h.name} <span className="muted">· {h.public_id}</span>
      </h1>
      <dl className="facts">
        <dt>Status</dt>
        <dd>{h.status.replace("_", " ")}</dd>
        <dt>GitHub</dt>
        <dd>{h.github_login ?? "—"}</dd>
        <dt>Email</dt>
        <dd>{h.email}</dd>
        <dt>Links</dt>
        <dd>
          {links.length
            ? links.map(([k, v]) => (
                <a key={k} href={String(v)} rel="noreferrer noopener" target="_blank">
                  {k}{" "}
                </a>
              ))
            : "—"}
        </dd>
        <dt>Role interest</dt>
        <dd>{h.role_interest ?? "—"}</dd>
        <dt id="header-city">City</dt>
        <dd>
          profile “{h.city ?? "—"}” · confirmed “{h.city_confirmed ?? "—"}”
          {h.city_first_submitted && ` · agent first sent “${h.city_first_submitted}”`}
        </dd>
        <dt>Earliest date of joining</dt>
        <dd>
          {h.earliest_start ?? "—"}
          {h.start_first_submitted && ` (agent first sent “${h.start_first_submitted}”)`}
        </dd>
        <dt>Current job</dt>
        <dd>
          {employment ? `${employment.role ?? ""} at ${employment.org ?? ""}` : "—"}
          {h.tenure_months != null && ` · ${h.tenure_months} months`}
        </dd>
        <dt>Thinking page</dt>
        <dd>
          {h.total_ms == null
            ? review.submitted
              ? "—"
              : "not finished"
            : `${seconds(h.total_ms)} total`}
          {h.extra_time && " · asked for extra time (not penalised)"}
        </dd>
      </dl>
    </section>
  );
}

export function Flags({ flags }: { flags: Flag[] }) {
  const groups: [string, Flag[]][] = [
    ["Boost", flags.filter((f) => f.kind === "boost")],
    ["Watch", flags.filter((f) => f.kind === "watch")],
  ];
  return (
    <section id="flags">
      <h2>Flags</h2>
      <p className="muted">
        Facts computed by rules, each linked to its evidence. Not conclusions.
      </p>
      {groups.map(([title, items]) => (
        <div key={title}>
          <h3>{title}</h3>
          {items.length === 0 ? (
            <p className="muted">None.</p>
          ) : (
            <ul>
              {items.map((f) => (
                <li key={f.id} className={`flag ${f.kind}`}>
                  <strong>{f.title}</strong> — {f.detail}{" "}
                  {f.evidence.map((anchor) => (
                    <a key={anchor} href={`#${anchor}`} className="evidence">
                      [{anchor}]
                    </a>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </section>
  );
}

function Metrics({ m, anchor }: { m: QuestionMetrics; anchor: string }) {
  return (
    <dl className="metrics" id={anchor}>
      <dt>First key</dt>
      <dd>{seconds(m.first_keystroke_ms)}</dd>
      <dt>Total</dt>
      <dd>{seconds(m.total_ms)}</dd>
      <dt>Pastes</dt>
      <dd>
        {m.paste_count} ({m.paste_chars} chars)
      </dd>
      <dt>Left page</dt>
      <dd>
        {m.focus_losses}× ({seconds(m.away_ms)})
      </dd>
      <dt>Keys / deletions</dt>
      <dd>
        {m.keys} / {m.deletions}
      </dd>
      <dt title="Median of per-batch medians; largest batch p90">Typing interval</dt>
      <dd>
        {m.median_interval_ms ?? "—"} ms median · {m.p90_interval_ms ?? "—"} ms p90
      </dd>
    </dl>
  );
}

function Card({ card }: { card: AnswerCard }) {
  const details = card.details as Record<string, unknown>;
  return (
    <article className="answer-card" id={card.anchor}>
      <h3>{card.title}</h3>
      {card.context && <blockquote className="context">{card.context}</blockquote>}
      {card.prompt && (
        <p className="prompt">
          {card.prompt}{" "}
          {card.prompt_source && <span className="muted">(source: {card.prompt_source})</span>}
        </p>
      )}
      <pre className="verbatim">{card.answer ?? "(no answer)"}</pre>
      <p className="muted">
        {card.timing.ms != null && `${seconds(card.timing.ms)} on this step`}
        {card.timing.auto_submitted && " · auto-submitted by the timer"}
        {card.timing.late && " · arrived after the limit"}
        {details.side ? ` · side: ${String(details.side)}` : ""}
        {details.fallacy
          ? ` · weak rebuttal's fallacy: ${String(details.fallacy).replace("_", " ")}`
          : ""}
        {Array.isArray(details.keywords_in_part_a) &&
          ` · tag keywords found in Part A: ${(details.keywords_in_part_a as string[]).join(", ") || "none"}`}
      </p>
      {card.metrics && <Metrics m={card.metrics} anchor={`metrics-${card.step}`} />}
      {card.rubric && <aside className="rubric">Rubric: {card.rubric}</aside>}
    </article>
  );
}

export function Answers({ review }: { review: CandidateReview }) {
  return (
    <section id="answers">
      <h2>Answers, verbatim</h2>
      {review.telemetry_settling && review.telemetry === "ok" && (
        <p className="notice" role="status">
          Submitted only minutes ago: telemetry may still be arriving, so metrics can be incomplete
          and "telemetry missing" is not flagged yet.
        </p>
      )}
      {review.telemetry === "unavailable" && (
        <p className="notice" role="status">
          Telemetry unavailable{review.telemetry_note ? `: ${review.telemetry_note}` : ""}. Answers
          are complete; metrics and telemetry-based flags are missing.
        </p>
      )}
      {review.answers.map((card) => (
        <Card key={card.anchor} card={card} />
      ))}
    </section>
  );
}

export function Fluency({
  fluency,
  review,
}: {
  fluency: AgentFluency | null;
  review: CandidateReview;
}) {
  return (
    <section id="fluency">
      <h2>Agent fluency</h2>
      {!fluency ? (
        <p className="muted">Needs telemetry.</p>
      ) : (
        <>
          <p>
            Client: <strong>{fluency.client_name ?? "unknown"}</strong> {fluency.client_version}
            {fluency.token_to_connect_ms != null &&
              ` · connected ${seconds(fluency.token_to_connect_ms)} after the token was issued`}
          </p>
          <ul>
            {fluency.recoveries.map((r, i) => (
              <li key={i}>
                {r.code} on {r.tool}:{" "}
                {r.recovered
                  ? `recovered after ${seconds(r.after_ms)} (${r.calls_until_recovered} call(s))`
                  : "never recovered"}
              </li>
            ))}
          </ul>
          <ol className="calls">
            {fluency.tool_calls.map((c, i) => (
              <li key={i}>
                +{seconds(c.at_ms)} {c.tool} → {c.success ? "ok" : `error ${c.code ?? ""}`}
              </li>
            ))}
          </ol>
        </>
      )}
      <p>
        City: agent first sent “{review.header.city_first_submitted ?? "—"}”, profile “
        {review.header.city ?? "—"}”, candidate confirmed “{review.header.city_confirmed ?? "—"}”.
      </p>
    </section>
  );
}

export function Curiosity({ review }: { review: CandidateReview }) {
  const reads = review.fluency?.resources_read ?? [];
  return (
    <section id="curiosity">
      <h2>Curiosity</h2>
      {review.findings.length === 0 && (
        <p className="muted">No eggs found (this is never a negative).</p>
      )}
      <ul>
        {review.findings.map((f) => (
          <li key={f.egg_id}>
            {f.label} <span className="muted">({f.channel})</span>
            {f.content && <pre className="verbatim">{f.content}</pre>}
          </li>
        ))}
      </ul>
      {reads.length > 0 && <p>Resources read: {reads.join(", ")}</p>}
    </section>
  );
}

export function Profile({ review }: { review: CandidateReview }) {
  const profile = review.profile as Record<string, unknown>;
  const lists = ["education", "projects", "internships", "certificates", "volunteering"] as const;
  return (
    <section id="profile">
      <h2>Profile as submitted by their agent</h2>
      {lists.map((kind) => {
        const items = (profile[kind] as Record<string, unknown>[] | undefined) ?? [];
        return (
          <div key={kind}>
            <h3>{kind}</h3>
            {items.length === 0 && <p className="muted">None.</p>}
            {items.map((item, i) => {
              const match = review.profile_cv_matches[`${kind}-${i}`];
              return (
                <pre key={i} id={`profile-${kind}-${i}`} className="verbatim">
                  {JSON.stringify(item, null, 2)}
                  {match !== undefined &&
                    `\n— ${match ? "found in CV text" : "NOT found in CV text"}`}
                </pre>
              );
            })}
          </div>
        );
      })}
      <details id="cv-text">
        <summary>CV text (extracted; no file is stored)</summary>
        <pre className="verbatim">{review.cv_text ?? "(none)"}</pre>
      </details>
    </section>
  );
}

export function Checks({ review }: { review: CandidateReview }) {
  return (
    <section id="checks">
      <h2>Checks</h2>
      <ul>
        {review.checks.map((c, i) => (
          <li key={i}>
            {c.kind} ({c.subject}): <strong>{c.result}</strong>
            {c.detail && ` ${JSON.stringify(c.detail)}`}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Timeline({ review }: { review: CandidateReview }) {
  return (
    <details id="timeline">
      <summary>Full timeline ({review.timeline.length} events)</summary>
      <ol className="timeline">
        {review.timeline.map((t, i) => (
          <li key={i}>
            <code>{new Date(t.at).toLocaleTimeString()}</code> {t.event}{" "}
            <span className="muted">{JSON.stringify(t.properties)}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}
